import React, { ForwardedRef, forwardRef, useEffect, useImperativeHandle, useRef, useState } from "react";
import GUI, { IGUIProps } from "../GUI";
import Select, { TPrimitiveId, TSelectRow } from "../Select/Select";
import {UpdateEvent} from "../GUIEvents"
import { usePath } from "../../libs/path/PathProvider";
import { TButtonSizes } from "../Button/Button";

export type TMenuItemClickHandler<ID extends TPrimitiveId> = (id:ID) => void
export type TUrl = string|{
    path?:string
    query?:Record<string,any>
    hash?:string
}
export type TMenuRow<ID extends TPrimitiveId> = (TSelectRow<ID> & {icon?:React.ReactNode,url?:TUrl,onClick?:TMenuItemClickHandler<ID>})| ID
export type TMenuProps<ID extends TPrimitiveId> = IGUIProps & {
    value?:ID
    defaultValue?:ID
    options:TMenuRow<ID>[]
    filter?:string
    size?:TButtonSizes
    itemProps?:(option:TMenuRow<ID>) => React.ComponentPropsWithoutRef<"div">
    itemRenderer?:(option:TMenuRow<ID>) => JSX.Element|string
    onUpdate?:(e:UpdateEvent<ID,HTMLDivElement>) => void
}
export interface IMenu<ID extends TPrimitiveId> {
    value:ID|undefined
    filter:(filter:string|undefined) => void
}

function filter<ID extends TPrimitiveId>(filter:string|undefined, options:TMenuRow<ID>[]) {
    if (!filter) return options
    const regex = new RegExp(filter, "i")
    return options.filter(item => regex.test(Select.text(item)))
}

export function useMenu<ID extends TPrimitiveId>(){
    const {setPath,setQuery,setHash} = usePath()
    const selectMenu = (option:TMenuRow<ID>) => {
        if (!Select.isSelect(option)) return
        if (option.url) {
            if (typeof(option.url)=="string") return setPath(option.url)
            const {path,query,hash} = option.url
            path && setPath(path)
            query && setQuery(query)
            hash && setHash(hash)
        }
        else if (option.onClick) option.onClick(Select.id(option))
    }
    return {selectMenu}
}

function Menu<ID extends TPrimitiveId>(props:TMenuProps<ID>, ref?:ForwardedRef<IMenu<ID>>){
    const [filtered,setFiltered] = useState<TMenuRow<ID>[]>(filter(props.filter, props.options))
    const [value,setValue] = useState<ID|undefined>(props.defaultValue||props.value)
    const {selectMenu} = useMenu<ID>()
    const refSelf = useRef<HTMLDivElement|null>(null)
    const mounted = useRef<boolean>(false)

    const onSelect = (option:TMenuRow<ID>, onClick?:React.MouseEventHandler<HTMLDivElement>) => (e:React.MouseEvent<HTMLDivElement>) => {
        const id = Select.id(option)
        setValue(id)
        props.onUpdate?.(new UpdateEvent(e.nativeEvent, id, refSelf.current))
        onClick?.(e)
        selectMenu(option)
    }
    const onDown = (onPointerDown?:React.PointerEventHandler<HTMLDivElement>) => (e:React.PointerEvent<HTMLDivElement>) => {
        e.preventDefault()
        onPointerDown?.(e)

    }
    const options = filtered.length ? filtered.map(option => {
        const {className,onClick,onPointerDown,...itemProps} = props.itemProps?.(option) || {}
        const id = Select.id(option)
        const icon = Select.isSelect(option) ? option.icon : <></>
        const content = props.itemRenderer?.(option) ?? <>{icon} {Select.text(option)}</>
        const menuItem = <div key={id} className={GUI.joinClass("menuItem",className,value==id ? "selected" : "")} onPointerDown={onDown(onPointerDown)} onClick={onSelect(option, onClick)} {...itemProps}>{content}</div>
        return menuItem
    }) : <div className="empty">List is empty</div>
    useImperativeHandle(ref, () => ({
        get value(){ return value},
        filter(text:string|undefined) { 
            setFiltered(filter(text, props.options)) 
        }
    }), [value])

    useEffect(() => {
        if (mounted.current) {
            if (value!=props.value) setValue(props.value)
            else setFiltered(filter(props.filter, props.options))
        }
        else mounted.current = true
    }, [props.value, props.options, props.filter])

    return <div className={GUI.joinClass("menu", props.className, props.size)} ref={refSelf}>{options}</div>
}

export default forwardRef(Menu) as <ID extends TPrimitiveId>(props:TMenuProps<ID> & {ref?:ForwardedRef<IMenu<ID>>}) => JSX.Element
